import React from 'react';

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  return `$${Math.round(n).toLocaleString()}`;
};

const compactList = (items) => items.filter(Boolean);

const getLast = (rows) => Array.isArray(rows) && rows.length > 0 ? rows[rows.length - 1] : null;

function buildBasicTransparency({ inputs = {}, results = {}, chartData = [] }) {
  const rows = (Array.isArray(chartData) && chartData.length ? chartData : results.chartData) || [];
  const finalRow = getLast(rows);
  return {
    inputs: compactList([
      'Household structure: single/couple status and partner details where supplied.',
      'Current ages and retirement horizon used to set the projection length and Age Pension timing.',
      'Current super balances for each member and combined household balance.',
      'Desired annual income or target income tested against the sustainable income result.',
      'Other taxable income, other non-taxable income, and assessable non-super assets entered by the user.',
      'Homeowner/non-homeowner status for Age Pension asset-test thresholds.',
      'Age Pension inclusion/exclusion setting.',
      'Age Pension eligibility age, maximum base pension, pension supplement, and energy supplement.',
      'Age Pension assets test thresholds/cut-offs for single/couple and homeowner/non-homeowner households.',
      'Age Pension income test thresholds/cut-offs and deemed income from assessable financial assets.',
      'Assumed investment return, inflation assumption, and a pension growth rate that grows future maximum pension rates, part-pension cut-offs, supplements, and rent assistance caps while keeping free areas and deeming thresholds CPI-flat in real terms (default 0.3%), plus retirement period.',
      'Fixed admin fees, percentage admin fees, investment fees, and insurance fees.',
      'Final balance target/remaining balance objective where supported by the basic projection path.',
      'Division 296 is not modelled in the Basic calculator; use Advanced, Accumulation, SMSF Division 296, or Adviser for large-balance Div 296 paths.',
    ]),
    rules: compactList([
      'Maximum Sustainable Income is solved from starting balance, period, return, inflation, fees, income, Age Pension settings, and final balance target.',
      'The Basic calculator runs a year-by-year projection across the selected retirement period to reconcile balances, withdrawals, pension income, other income, fees, returns, and final balance.',
      'Age Pension is calculated only when enabled and eligible; otherwise the pension component is zero.',
      'The lower result from the Age Pension assets test and income test limits the pension estimate.',
      'Pension supplements and energy supplement are added where eligible in the shared pension engine.',
      'Future Age Pension maximum rates, part-pension cut-offs, supplements, and rent assistance caps grow at the pension growth rate; asset/income free areas and deeming thresholds stay CPI-flat in real terms; deeming percentages stay fixed.',
      'Basic calculator screens do not currently expose a rent-paid input, so Commonwealth Rent Assistance is not part of the normal Basic UI calculation.',
      "All projected dollar results are presented in today's dollars after inflation adjustment.",
      'Fees reduce the projected balance before sustainable income and final balance are assessed.',
      'Division 296 additional tax is out of scope for Basic; balances above $3m should be stress-tested in Advanced or the dedicated Division 296 tool.',
    ]),
    yearly: compactList([
      rows.length ? `${rows.length} year-by-year projection rows generated.` : 'Year-by-year projection rows are produced after a Basic calculation run; this standalone transparency list is not attached to a live run.',
      finalRow ? `Final balance: ${money(finalRow.endingBalance ?? finalRow.balance)}.` : null,
      rows.some((r) => (r.pension || 0) > 0) ? 'At least one year includes Age Pension income.' : 'No Age Pension income appears in the yearly projection.',
      rows.some((r) => (r.withdrawalFromSuper || r.withdrawal || 0) > 0) ? 'Super withdrawals are shown year by year.' : null,
    ]),
  };
}

function buildAdvancedTransparency({ inputs = {}, results = {}, chartData = [] }) {
  const rows = chartData || results.incomeByYear || results.yearlyData || results.chartData || [];
  const finalRow = getLast(rows);
  return {
    inputs: compactList([
      'Household structure: single/couple status, both partners ages, genders, retirement ages, and eligibility timing.',
      'Life expectancy/mortality reference data used for planning context and timeline display.',
      'Current super balances for each partner, pension-phase access, preservation-age access, and optional withdrawal-before-retirement-age control.',
      'Bridge / delayed super access settings for the Fixed strategy: member super access ages, bridge cash bucket, outside-super investment bucket, and outside-super real return assumption.',
      'Mortgage payoff strategy comparison for the Fixed strategy: mortgage balance, interest rate, normal annual repayments, payoff timing, payoff funding source, partial payoff amount, and outside-cash contribute-instead comparison where applicable.',
      'Transfer Balance Cap used for each partner and general Transfer Balance Cap compliance context.',
      'Other taxable income, other non-taxable income, assessable non-super assets, and SMSF/non-earning asset values transferred from SMSF where applicable.',
      'Homeowner/non-homeowner status plus fortnightly rent paid for Commonwealth Rent Assistance where the household is a renter.',
      'Age Pension inclusion/exclusion, eligibility age, base pension, pension supplement, energy supplement, asset test, income test, deeming, Work Bonus, and Rent Assistance.',
      'Defined benefit pension income, annuity purchase price, annuity annual payment, and whether those streams are indexed.',
      'Part-time employment income, Work Bonus treatment, phased retirement wages, partner wage timing, and employer SG contributions while still working.',
      'Withdrawal strategy: fixed, floor/ceiling, Vanguard guardrails, dynamic SWR, or other available strategy settings.',
      'Target final balance and retirement period/start year.',
      'Historical return sequence, CPI/inflation sequence, Monte Carlo sample path assumptions, and rolling-period start years.',
      'Asset allocation across non-earning assets, S&P 500, Australian shares, international shares, bonds, cash, and franking credit assumptions.',
      'Alternative assets: residential property, commercial property, collectibles, crypto, commodities, other alternatives, infrastructure value, infrastructure allocation, income yield, and capital growth settings where enabled.',
      'Rental income from property/alternatives and infrastructure dividends/returns where entered.',
      'Lump sum withdrawals: schedule type, schedule age/year, amount, label, and whether the row falls inside the projection period.',
      'Lump sum super contributions: recipient, contribution type, schedule age/year, amount, label, and cap/compliance flagging.',
      'Fees: fixed admin fee, admin percentage, investment fee percentage, investment fee cap, transaction costs, and insurance fees.',
      'What-if scenarios: retirement period change, fee change, sequence-risk shock, target income test, and related strategy comparison outputs.',
      'Division 296 (from 1 July 2026): Total Super Balance path, realised/reported earnings basis where available, indexed $3m / $10m thresholds, and additional tax tiers on earnings above those thresholds.',
    ]),
    rules: compactList([
      "Historical simulations use the selected start year's market return and CPI/inflation sequence where available.",
      'Monte Carlo keeps return and CPI observations paired so market and inflation conditions remain internally consistent.',
      'Age Pension uses eligibility age, household status, homeowner/renter status, assessable assets, deemed income, other income, supplements, Work Bonus, and Commonwealth Rent Assistance where applicable.',
      'Future Age Pension maximum rates, part-pension cut-offs, supplements, and rent assistance caps grow at the pension growth rate; asset/income free areas and deeming thresholds stay CPI-flat in real terms; deeming percentages stay fixed.',
      'The lower Age Pension entitlement from the assets test and income test is used before supplements and Rent Assistance are added.',
      'Rent Assistance is calculated only for non-homeowners with rent entered and only when Age Pension eligibility exists.',
      'Minimum super drawdown rules can force withdrawals above the strategy target in later years.',
      'When the bridge strategy is enabled in Fixed mode, required income is drawn from bridge cash first, outside-super investments second, then accessible super; rows flag bridge depletion before super access.',
      'When mortgage comparison is enabled in Fixed mode, mortgage repayments and payoff amounts are treated as required outflows; safe income is reported as lifestyle income after those mortgage costs. Super-funded payoff is not treated as income, and the contribute-instead alternative is only shown for outside-cash payoff scenarios.',
      'Phased retirement includes employment income and employer SG until each partner retires.',
      'Super balances are adjusted year by year for withdrawals, lump sums, contributions, fees, investment returns, inflation, and pension/accumulation phase assumptions.',
      'Contribution rules flag concessional, non-concessional, downsizer, and spouse contribution issues where the scheduled amounts exceed configured limits or interact with SG.',
      'Strategy rules can change annual income under floor/ceiling, guardrails, dynamic SWR, and minimum drawdown constraints.',
      'Alternative assets can affect assessable assets, portfolio returns, rental/infrastructure income, and liquid-super drawdown ordering.',
      'Division 296 additional tax applies when enabled for the simulation financial year (from 2026-27): +15% on earnings attributable to the balance above the large super balance threshold, and a further +10% above the very large threshold. 2026-27 uses year-end TSB for the proportion test; later years use the higher of start and end TSB.',
      'Division 296 uses realised/fund earnings where the engine supplies them; simulation-path earnings attribution is a planning proxy. Member-level attribution detail and some SMSF elections remain subject to final regulations and ATO guidance.',
    ]),
    yearly: compactList([
      rows.length ? `${rows.length} detailed projection rows generated.` : 'No yearly projection rows are available.',
      finalRow ? `Final balance: ${money(finalRow.balance ?? results.finalBalance)}.` : null,
      rows.some((r) => (r.contributionFlags || []).length > 0) ? 'One or more contribution cap flags were produced in the yearly table.' : 'No contribution cap flags were produced.',
      rows.some((r) => (r.lumpSumWithdrawal || 0) > 0) ? 'Lump sum withdrawal years are visible in results, charts, and exports.' : null,
      rows.some((r) => (r.lumpSumContribution || 0) > 0) ? 'Lump sum contribution years are visible in results, charts, and exports.' : null,
      rows.some((r) => (r.bridgeWithdrawal || 0) > 0) ? 'Bridge bucket withdrawals are visible in yearly rows and income-source charts.' : null,
      rows.some((r) => r.bridgeDepletedBeforeAccess || (r.bridgeShortfall || 0) > 0) ? 'At least one row flagged bridge depletion or a bridge shortfall before super access.' : null,
      rows.some((r) => (r.mortgageOpeningBalance || 0) > 0 || (r.mortgageOutflow || 0) > 0) ? 'Mortgage balances, interest, repayments, payoff cashflows, and after-mortgage lifestyle income are visible in yearly rows.' : null,
      rows.some((r) => (r.division296Tax || r.div296Tax || 0) > 0) ? 'At least one year includes Division 296 additional tax.' : null,
    ]),
  };
}

function buildAccumulationTransparency({ inputs = {}, results = {} }) {
  const rows = results.yearlyData || results.combinedData || results.chartData || [];
  const finalRow = getLast(rows);
  return {
    inputs: compactList([
      'Household structure: single/couple status and partner fields where enabled.',
      'Current age, retirement age, partner current age, and partner retirement age.',
      'Current super balances for each member.',
      'Current wage/salary for each member and wage-growth assumption.',
      'Employer SG contributions derived from wage inputs.',
      'Salary sacrifice/personal deductible contributions for each member.',
      'Non-concessional contributions for each member.',
      'Carry-forward concessional contribution inputs and unused concessional cap amounts.',
      'Bring-forward non-concessional contribution option and related cap capacity.',
      'Downsizer contribution amount and downsizer year.',
      'Spouse contribution inputs and possible spouse contribution tax offset.',
      'Government co-contribution and LISTO options plus adjusted taxable income fields where supplied.',
      'Transition-to-retirement option, TTR start age, TTR drawdown rate, and accumulation/pension phase split.',
      'Work-test setting for contribution eligibility at older ages.',
      'Investment mode: assumed return or historical/portfolio mode where available.',
      'Asset allocation across S&P 500, Australian shares, international shares, bonds, cash, non-earning assets, and franking credit assumptions.',
      'Portfolio turnover and dividend-portion inputs where used for tax/franking modelling.',
      'Fees: fixed admin fee, admin percentage, investment fees, and insurance fees.',
      'Inflation assumption used to express projected balances in today-dollar terms.',
      'Division 296 context for high Total Super Balance paths in later simulation years (from 2026-27), where the accumulation engine applies the additional earnings tax tiers.',
    ]),
    rules: compactList([
      'Projection grows accumulation balances from current age to retirement age using wages, SG, selected contributions, investment returns, inflation, and fees.',
      'Employer SG is included from wage inputs where applicable.',
      'Concessional contributions combine SG and personal/salary-sacrifice amounts for cap context.',
      'Non-concessional, bring-forward, downsizer, spouse contribution, co-contribution, LISTO, TTR, and work-test rules are considered when their inputs/options are enabled.',
      'Partner projections are run separately where couple mode is enabled, then combined for household totals.',
      'Investment returns, asset allocation, franking assumptions, fees, and inflation affect projected balances.',
      "All displayed balances are intended as today's-dollar estimates after inflation assumptions.",
      'Where Division 296 is enabled for a simulation year, additional tax on earnings attributable to balances above the indexed $3m / $10m thresholds can reduce net accumulation growth. Attribution of member earnings remains provisional pending final ATO guidance.',
    ]),
    yearly: compactList([
      rows.length ? `${rows.length} yearly accumulation rows generated.` : 'No yearly rows are available.',
      finalRow ? `Final projected balance/wealth: ${money(finalRow.totalBalance ?? finalRow.balance ?? finalRow.userBalance ?? results.totalFinalBalance ?? results.finalBalances?.total)}.` : null,
      results.totalContributions ? `Total contributions modelled: ${money(results.totalContributions)}.` : null,
      results.avgAnnualContributions ? `Average annual contributions: ${money(results.avgAnnualContributions)}.` : null,
    ]),
  };
}

function getSMSFConsideredItems(title = 'SMSF calculator') {
  const key = title.toLowerCase();
  if (key.includes('division 293')) {
    return [
      'Assessable income.',
      'Reportable fringe benefits.',
      'Reportable employer super contributions.',
      'Net investment losses.',
      'Concessional contributions.',
      'Income for surcharge purposes.',
      'Division 293 income threshold.',
      'Taxable contribution amount limited by excess income and concessional contributions.',
      'Division 293 tax rate.',
      'Release authority and personal payment options.',
      'Deferral/interest scenario where shown.',
      'Five-year projection of income, contributions, taxable amount, and tax liability.',
      'Reduction strategies such as reducing concessional contributions or timing contributions.',
    ];
  }
  if (key.includes('division 296')) {
    return [
      'Total Super Balance at start and end of year.',
      'Large Super Balance Threshold and Very Large Super Balance Threshold (indexed).',
      'Division 296 earnings mode: reported earnings, fund-builder earnings, or TSB movement proxy.',
      'Fund earnings inputs: taxable income, assessable contributions, net ECPI, withdrawals, and contributions where applicable.',
      'Proportion of TSB above the large super balance threshold (2026-27 transitional: year-end TSB; later years: higher of start and end).',
      'Division 296 tax rate and additional very-large-balance tier where applicable.',
      'Release authority percentage, release cap, and personal payment amount.',
      'Five-year projection using balance growth, indexed thresholds, same contributions, and same withdrawal assumptions.',
      'Caveat: member-level earnings attribution and some SMSF election detail remain subject to final regulations and ATO guidance.',
    ];
  }
  if (key.includes('bring-forward')) {
    return [
      'Member age.',
      'Total Super Balance.',
      'Current non-concessional contribution cap.',
      'Bring-forward eligibility age limit.',
      'Three-year bring-forward cap capacity.',
      'Year 1, year 2, and year 3 planned non-concessional contributions.',
      'Existing bring-forward window and years remaining where entered.',
      'Remaining contribution capacity, utilisation percentage, warnings, and optimisation suggestions.',
    ];
  }
  if (key.includes('fhss')) {
    return [
      'Voluntary concessional contributions by year.',
      'Voluntary non-concessional contributions by year.',
      'Annual FHSS contribution limits.',
      'Total FHSS release cap.',
      'Deemed earnings calculation.',
      'Withheld tax on concessional amounts.',
      'Net estimated amount available for home deposit.',
      'Eligibility checklist items such as first home buyer status and contract/occupancy requirements.',
    ];
  }
  if (key.includes('lrba')) {
    return [
      'Property purchase price.',
      'Loan amount and loan-to-value ratio.',
      'Interest rate, loan term, repayment type, and interest-only period.',
      'Rental income, rental growth, property expenses, and expense growth.',
      'Stamp duty, establishment costs, legal fees, valuation fees, and total acquisition cost.',
      'Required equity/deposit and upfront costs.',
      'Annual repayment, interest, principal, cash flow, and rental yield.',
      'LRBA compliance checks including LVR limit, arm-length terms, sole purpose test, single acquirable asset, and liquidity/cash-flow warnings.',
      'Property value, loan balance, equity, capital gain, and cumulative cash-flow projections.',
    ];
  }
  if (key.includes('carry-forward')) {
    return [
      'Total Super Balance threshold for carry-forward eligibility.',
      'Current year concessional contribution cap.',
      'Current year concessional contributions already made.',
      'Unused concessional cap amounts from the previous five years.',
      'Expiry timing for unused amounts.',
      'Total available concessional cap capacity.',
      'Planned extra contribution and potential tax saving based on marginal tax rate.',
      'Recommended contribution strategies and urgency flags.',
    ];
  }
  if (key.includes('co-contribution')) {
    return [
      'Adjusted taxable income / total income.',
      'Personal after-tax super contribution.',
      'Total Super Balance eligibility.',
      'Age and residency/eligibility flags where entered.',
      'Government co-contribution lower and upper income thresholds.',
      'Maximum co-contribution amount and taper rate.',
      'Optimal personal contribution to maximise government co-contribution.',
      'Estimated lifetime value using assumed return and years to retirement.',
      'Eligibility warnings and errors.',
    ];
  }
  if (key.includes('downsizer')) {
    return [
      'Member age.',
      'Home ownership period and sale timing.',
      'Sale proceeds and proposed downsizer contribution.',
      'Personal and spouse contribution split.',
      'Downsizer contribution cap.',
      '90-day contribution deadline.',
      'Total Super Balance context, noting downsizer contributions sit outside normal contribution caps.',
      'Future value estimate and alternative uses comparison.',
      'Eligibility warnings and errors.',
    ];
  }
  if (key.includes('setup')) {
    return [
      'Fund name, ABN, TFN, trustee/member details, and member ages.',
      'Initial member balances and total fund balance.',
      'Bank details where entered.',
      'Investment strategy risk profile and asset allocation.',
      'Estimated fee context, age-based recommendations, insurance context, pension phase availability, and LRBA opportunity prompts.',
    ];
  }
  return [
    'Fund/member identity and member count where supplied.',
    'Fund balance, member balances, and Total Super Balance values where supplied.',
    'Contribution amounts, contribution caps, eligibility thresholds, and tax rates relevant to the selected SMSF tool.',
    'Property, LRBA, pension, investment, tax, compliance, insurance, estate, and reporting fields relevant to the selected SMSF module.',
    'Warnings, errors, recommendations, and projection/detail rows produced by that module.',
  ];
}

function buildSMSFTransparency({ inputs = {}, results = {}, title = 'SMSF calculator' }) {
  const rows = results.yearlyData || results.projections || results.chartData || results.tableData || [];
  const isDiv296 = String(title || '').toLowerCase().includes('division 296');
  return {
    inputs: compactList([
      `Calculator: ${title}`,
      ...getSMSFConsideredItems(title),
    ]),
    rules: compactList([
      'SMSF outputs are calculated from the entered fund/member values and the rule module for this specific SMSF calculator.',
      'Where relevant, the calculator applies configured ATO-style thresholds, caps, tax rates, age limits, TSB thresholds, and eligibility checks from the project rules configuration.',
      isDiv296
        ? 'Division 296 is enacted from 1 July 2026 on realised/fund earnings (not unrealised paper gains). Prefer reported Division 296 fund earnings; fund-builder and TSB-proxy modes are estimates. Member attribution and SMSF grandfathering elections remain subject to final regulations and ATO guidance.'
        : null,
      'Warnings, errors, and recommendations are surfaced as informational compliance flags and do not replace trustee, tax, legal, or financial advice.',
      "If a field or optional strategy was not entered/enabled, it is not included in that calculator's result.",
    ]),
    yearly: compactList([
      rows.length ? `${rows.length} projection/detail rows generated.` : 'This SMSF calculator produces a point-in-time result or checklist rather than a yearly projection.',
      results.recommendations?.length ? `${results.recommendations.length} recommendation(s) produced.` : null,
      results.warnings?.length ? `${results.warnings.length} warning(s) produced.` : null,
    ]),
  };
}

function buildAdviserTransparency(args = {}) {
  const base = buildAdvancedTransparency({ ...args, chartData: [], results: {} });
  return {
    inputs: compactList([
      'Adviser desk licence session: practice-labelled client meeting layout (Your Plan, How It Plays Out, Basic View, Stress Test, What\'s included, adviser-only Details).',
      ...base.inputs,
      'Division 296 large-balance paths using the same Advanced engine rules (enabled from 2026-27 financial years).',
      'PDF/CSV export assumptions and disclaimer wording for the advice file artefact.',
    ]),
    rules: compactList([
      'Uses the Advanced Retirement modelling engine inside an adviser meeting shell; educational modelling only — the practice AFSL owns advice and SOA responsibility.',
      ...base.rules,
      'Client-facing tabs hide raw input controls until the adviser unlocks Details; What\'s included lists model scope at any time, including before a run.',
      'Scenarios run in-browser; client modelling inputs are not uploaded to run the engine. Billing and desk-seat emails are handled separately for licensing.',
    ]),
    yearly: compactList([
      'Stress Test tab surfaces historical sequence outcomes and Monte Carlo dual hit rates for the same income target.',
    ]),
  };
}

export function buildTransparencySections(kind, args = {}) {
  if (kind === 'adviser') return buildAdviserTransparency(args);
  if (kind === 'advanced') return buildAdvancedTransparency(args);
  if (kind === 'accumulation') return buildAccumulationTransparency(args);
  if (kind === 'smsf') return buildSMSFTransparency(args);
  return buildBasicTransparency(args);
}

export default function CalculationTransparency({
  kind = 'basic',
  inputs = {},
  results = {},
  chartData = [],
  title,
  className = '',
  defaultOpen = true,
  yearlyHeading = 'Result Trace',
  summaryHint = 'Comprehensive list of what this calculator can consider, plus the rules and result trace.',
}) {
  const sections = buildTransparencySections(kind, { inputs, results, chartData, title });
  const hasAny = sections.inputs.length || sections.rules.length || sections.yearly.length;
  if (!hasAny) return null;

  const heading = title || 'Calculation Transparency';

  return (
    <details open={defaultOpen} className={`bg-slate-50 border border-slate-200 rounded-lg overflow-hidden ${className}`}>
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-100">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{heading}</h3>
          <p className="text-xs text-slate-600 mt-0.5">{summaryHint}</p>
        </div>
        <span className="text-slate-500" aria-hidden="true">v</span>
      </summary>
      <div className="border-t border-slate-200 p-4 space-y-4 text-sm text-slate-800">
        <div>
          <h4 className="font-semibold text-slate-900 mb-1">Everything This Calculator Can Consider</h4>
          <ul className="list-disc ml-5 space-y-1">
            {sections.inputs.map((item, idx) => <li key={`inputs-${idx}`}>{item}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-1">Rules Applied</h4>
          <ul className="list-disc ml-5 space-y-1">
            {sections.rules.map((item, idx) => <li key={`rules-${idx}`}>{item}</li>)}
          </ul>
        </div>
        {sections.yearly.length > 0 && (
          <div>
            <h4 className="font-semibold text-slate-900 mb-1">{yearlyHeading}</h4>
            <ul className="list-disc ml-5 space-y-1">
              {sections.yearly.map((item, idx) => <li key={`yearly-${idx}`}>{item}</li>)}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}
